import React from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import MoveIcon from 'src/shared/material_icons/content/forward.svg';
import { DuplicateIcon, TrashIcon } from 'src/components/widgets/icons/scene';
import { SelectionCommand, SelectionCommands } from '../scene-editors/selectionInspector';
import AreaIcon from 'src/shared/material_icons/maps/category.svg';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { FormControl } from '../../../../../widgets/formControl';
import { BasicButton } from '../../../../../widgets/buttons';
import { Input } from '../../../../../widgets/input';
import { AdvancedSelect, compactSelectStyles } from '../../../../../widgets/select';
import { PropertiesPanelNumberField } from '../scene-properties-panel';
import { MessageDialog, useDialogManager } from '../../../../../widgets/dialog';
import { showInfoNotification, showWarningNotification } from '../../../../../widgets/notificationToaster';
import { TextArea } from '../../../../../widgets/textArea';
import { Tab, TabList, TabPanel } from '../../../../../widgets/ztabs';
import useComposerStore from '../../configurator-composer/composer/composerStore';
import { ComposerAreaRowAction, ComposerAreaTreeElement } from '../../configurator-composer/composer/components/composerAreaTreeElement';
import ComposerUploadComponent from '../../configurator-composer/composer/components/composerUploadComponent';
import { getComposerDefaultTemplate } from '../../configurator-composer/composer/helpers/composerDefaultTemplate';
import { ComposerAreaNode, ComposerAttributeNode, ComposerAttributeType, isComposerAreaLinkedToAttribute } from './attributes/composerAttributesModels';
import ComposerLinksEditor from './attributes/composerLinksEditor';
import { ComposerRevealButton } from './composerReveal';
import { addComposerArea } from './attributes/composerAddChild';
import { ComposerCameraSlot, ComposerChoice, ComposerObjectSlots, ComposerSlotOption } from './composerSlots';
import { carriedFrom, startPointerReorder } from '../pointerReorder';
import { ComposerAreaMark } from '../../configurator-composer/composer/components/composerAttributeTreeElement';
import {
    ComposerValidationErrorCode,
    hasComposerValidationError,
    isPrintableComposerMesh
} from '../../configurator-composer/composer/helpers/composerAttributeValidation';
import {
    ComposerFormStack,
    ComposerKindBadge,
    ComposerPanel,
    ComposerSelectionBody,
    ComposerSection,
    ComposerSelectionHeader,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectName,
    ComposerSubjectNameInput,
    ComposerTabCount,
    ComposerTabs,
    ComposerPresetBox,
    ComposerPresetButtons,
    ComposerFieldNote,
    ComposerUnsupportedNote
} from './composerWorkspace';
import { ConfirmTwice, confirmLabel, isConfirmedRun } from '../confirmTwice';
import { EditorDialog } from '../editorDialogWindow';

const MoveAreaDialog: React.FC<{
    label: string;
    targets: ComposerAttributeNode[];
    onConfirm: (target: ComposerAttributeNode) => void;
}> = ({ label, targets, onConfirm }) => {
    const [targetID, setTargetID] = React.useState(targets[0]?.id ?? '');
    const selectedTarget = targets.find((target) => target.id === targetID);
    return (
        <EditorDialog
            title={T._('Move to another attribute', 'Admin')}
            size='small'
            buttons={[{ label: T._('Move', 'Admin'), disabled: !selectedTarget, onClick: () => selectedTarget && onConfirm(selectedTarget) }]}
        >
            <div style={{ padding: 24 }}>
                <AdvancedSelect
                    aria-label={stringFormat(T._('Destination for {0}', 'Admin'), label)}
                    options={targets.map((target) => ({ value: target.id, label: target.name }))}
                    value={selectedTarget ? { value: selectedTarget.id, label: selectedTarget.name } : null}
                    onChange={(option) => setTargetID(option?.value ?? '')}
                    styles={compactSelectStyles as any}
                    menuPortalTarget={document.body}
                    menuPlacement='auto'
                />
            </div>
        </EditorDialog>
    );
};

const isAreaAttribute = (attribute: ComposerAttributeNode) =>
    attribute.type === ComposerAttributeType.Attribute && attribute.attributeType === MPlaza.AttributeType.CustomizableArea;

/**
 * I COMANDI SU UN MUCCHIO DI AREE (utente, 2026-10-15): spostare, clonare e togliere valgono per
 * un'area sola come per una selezione — anche di attributi diversi —, così il «…» di una riga scelta
 * insieme ad altre e il pannello della selezione multipla fanno la stessa cosa.
 */
export const useAreaSelectionCommands = () => {
    const { showDialog, closeDialog } = useDialogManager();
    const [updateArea, cloneArea, removeArea, selectAttributes, selectOptions, selectAreas, setAttributeFace] = useComposerStore((store) => [
        store.updateArea,
        store.cloneArea,
        store.removeArea,
        store.selectAttributes,
        store.selectOptions,
        store.selectAreas,
        store.setAttributeFace
    ]);

    const ownerOf = (area: ComposerAreaNode) => useComposerStore.getState().attributes.find((attribute) => isComposerAreaLinkedToAttribute(area, attribute));

    // Le aree scelte stanno sotto l'attributo che le contiene: è lui che l'albero accende come scia.
    const chooseAreas = (picked: ComposerAreaNode[]) => {
        const owner = picked.length ? ownerOf(picked[picked.length - 1]) : undefined;
        if (owner) selectAttributes([owner]);
        selectOptions(null);
        selectAreas(picked);
        setAttributeFace('areas');
    };

    const moveAreas = (subjects: ComposerAreaNode[]) => {
        const owners = new Set(subjects.map((area) => ownerOf(area)?.id));
        const targets = useComposerStore
            .getState()
            .attributes.filter((attribute) => isAreaAttribute(attribute) && !(owners.size === 1 && owners.has(attribute.id)));
        if (targets.length === 0) {
            showInfoNotification({ message: T._('Create another customizable-area attribute before moving this area.', 'Admin') });
            return;
        }
        showDialog(
            'composerMoveArea',
            <MoveAreaDialog
                label={subjects.length === 1 ? subjects[0].name : stringFormat(T._('{0} areas', 'Admin'), subjects.length.toString())}
                targets={targets}
                onConfirm={(target) => {
                    const start = useComposerStore.getState().areas.filter((area) => isComposerAreaLinkedToAttribute(area, target)).length;
                    const moved = subjects.map((area, index) => ({
                        ...area,
                        attributeID: target.idNumeric,
                        attributeGuid: target.id,
                        displayOrder: start + index
                    }));
                    moved.forEach(updateArea);
                    chooseAreas(moved);
                    closeDialog('composerMoveArea');
                }}
            />
        );
    };

    const cloneAreas = (subjects: ComposerAreaNode[]) => {
        const clones = subjects.map((area) => {
            const siblings = useComposerStore.getState().areas.filter((candidate) => candidate.attributeGuid === area.attributeGuid);
            const cloned = cloneArea(area);
            const placed = {
                ...cloned,
                attributeID: area.attributeID,
                attributeGuid: area.attributeGuid,
                displayOrder: Math.max(-1, ...siblings.map((candidate) => candidate.displayOrder)) + 1
            };
            updateArea(placed);
            return placed;
        });
        chooseAreas(clones);
    };

    const removeAreas = (subjects: ComposerAreaNode[]) => {
        const run = () => {
            subjects.forEach((area) => removeArea(area.id));
            selectAreas(null);
        };
        // Dal menu o dal bottone si chiede due volte, senza finestra (confirmTwice): la domanda è già fatta.
        if (isConfirmedRun()) return run();
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={
                    subjects.length === 1
                        ? stringFormat(T._('Are you sure you want to remove “{0}”?', 'Admin'), subjects[0].name)
                        : stringFormat(T._('Are you sure you want to remove {0} areas?', 'Admin'), subjects.length.toString())
                }
                buttons={[
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            run();
                        }
                    }
                ]}
            />
        );
    };

    return { chooseAreas, moveAreas, cloneAreas, removeAreas };
};

/** Le aree di un attributo «Customizable area», come righe dell'albero della struttura. */
export const ComposerAreaRows: React.FC<{
    attribute: ComposerAttributeNode;
    indent: number;
    /** L'ultima riga premuta senza ⇧ nell'albero: la tiene l'albero, che vede anche gli altri attributi. */
    rangeAnchorRef?: React.MutableRefObject<string | null>;
    /** ⇧click: l'intervallo lo calcola l'albero, sulle righe visibili e oltre questo attributo. */
    onRangeSelect?: (area: ComposerAreaNode) => void;
    /** La profondità delle righe nell'albero (quella dell'attributo più uno), per il trascinamento. */
    level?: number;
}> = ({ attribute, indent, rangeAnchorRef, onRangeSelect, level = 1 }) => {
    const [areas, selectedAreas, updateArea] = useComposerStore((store) => [store.areas, store.selectedAreas, store.updateArea]);
    const { chooseAreas, moveAreas, cloneAreas, removeAreas } = useAreaSelectionCommands();
    const linkedAreas = areas.filter((area) => isComposerAreaLinkedToAttribute(area, attribute)).sort((left, right) => left.displayOrder - right.displayOrder);

    // ⇧ E ⌘ COME LE OPZIONI (utente, 2026-10-15): ⇧ prende il tratto, ⌘ aggiunge o toglie una riga.
    const select = (event: React.MouseEvent, area: ComposerAreaNode) => {
        if (event.shiftKey && onRangeSelect) {
            onRangeSelect(area);
            return;
        }
        if (rangeAnchorRef) rangeAnchorRef.current = area.id;
        if (event.ctrlKey || event.metaKey) {
            const current = useComposerStore.getState().selectedAreas ?? [];
            chooseAreas(current.some((candidate) => candidate.id === area.id) ? current.filter((candidate) => candidate.id !== area.id) : [...current, area]);
            return;
        }
        chooseAreas([area]);
    };

    // IL «…» DI UNA RIGA SCELTA INSIEME AD ALTRE VALE PER TUTTA LA SELEZIONE, come per le opzioni.
    const subjectOf = (area: ComposerAreaNode) => {
        const all = selectedAreas ?? [];
        return all.length > 1 && all.some((candidate) => candidate.id === area.id) ? all : [area];
    };

    const onAreaRowAction = (action: ComposerAreaRowAction, area: ComposerAreaNode) => {
        if (action === 'move') return moveAreas(subjectOf(area));
        if (action === 'duplicate') return cloneAreas(subjectOf(area));
        removeAreas(subjectOf(area));
    };

    // IL TRASCINAMENTO (pointerReorder): un'area si riordina nel suo attributo o passa a un altro attributo
    // «Customizable area» (utente, 2026-10-15) — davanti o dopo una sua area, o dentro la sua riga, in coda.
    // Se è scelta porta con sé tutta la selezione, anche quella sotto altri attributi. «Move…» resta nel «…».
    const startDrag = (event: React.PointerEvent<HTMLElement>, dragged: ComposerAreaNode) => {
        const chosen = (selectedAreas ?? []).map((area) => area.id);
        const scope = event.currentTarget.closest('[data-reorder-scope]');
        const treeOrder = Array.from(scope?.querySelectorAll<HTMLElement>('[data-reorder-id]') ?? []).map((row) => row.dataset.reorderId ?? '');
        const carriedIds = carriedFrom([...treeOrder, ...chosen.filter((id) => !treeOrder.includes(id))], dragged.id, chosen);
        const state = () => useComposerStore.getState();
        const ownerOf = (areaID: string) => {
            const area = state().areas.find((candidate) => candidate.id === areaID);
            return area ? state().attributes.find((attribute) => isAreaAttribute(attribute) && isComposerAreaLinkedToAttribute(area, attribute)) : undefined;
        };
        startPointerReorder(event, {
            fromRow: true,
            draggedId: dragged.id,
            carriedIds,
            canDrop: (targetId, position) => {
                if (carriedIds.includes(targetId)) return false;
                if (position === 'into') return state().attributes.some((attribute) => attribute.id === targetId && isAreaAttribute(attribute));
                return !!ownerOf(targetId);
            },
            onDrop: (targetId, position) => {
                const target = position === 'into' ? state().attributes.find((attribute) => attribute.id === targetId) : ownerOf(targetId);
                if (!target) return;
                const all = state().areas;
                const ordered = all
                    .filter((area) => isComposerAreaLinkedToAttribute(area, target))
                    .sort((left, right) => left.displayOrder - right.displayOrder)
                    .map((area) => area.id)
                    .filter((id) => !carriedIds.includes(id));
                const anchor = position === 'into' ? -1 : ordered.indexOf(targetId);
                const at = anchor < 0 ? ordered.length : position === 'after' ? anchor + 1 : anchor;
                const next = [...ordered.slice(0, at), ...carriedIds, ...ordered.slice(at)];
                const placed = next.flatMap((id, displayOrder) => {
                    const area = all.find((candidate) => candidate.id === id);
                    return area ? [{ ...area, attributeID: target.idNumeric, attributeGuid: target.id, displayOrder }] : [];
                });
                placed.forEach((area) => {
                    const before = all.find((candidate) => candidate.id === area.id);
                    if (before?.displayOrder !== area.displayOrder || before.attributeGuid !== area.attributeGuid || before.attributeID !== area.attributeID)
                        updateArea(area);
                });
                chooseAreas(placed.filter((area) => carriedIds.includes(area.id)));
            }
        });
    };

    return (
        <>
            {linkedAreas.map((area) => (
                <ComposerAreaTreeElement
                    key={area.id}
                    area={area}
                    indent={indent}
                    selected={selectedAreas?.some((candidate) => candidate.id === area.id) ?? false}
                    onSelect={select}
                    onDoubleClick={(picked) => chooseAreas([picked])}
                    onAction={onAreaRowAction}
                    level={level}
                    onRowPointerDown={startDrag}
                />
            ))}
        </>
    );
};

/** PIÙ AREE SCELTE: la testata del mucchio e i comandi che valgono per tutte, come per le opzioni. */
export const MultipleAreasPanel: React.FC<{ areas: ComposerAreaNode[] }> = ({ areas: subjects }) => {
    const { chooseAreas, moveAreas, cloneAreas, removeAreas } = useAreaSelectionCommands();
    return (
        <ComposerPanel>
            <ComposerSelectionHeader
                kind='option'
                entries={subjects.map((area) => ({
                    id: area.id,
                    name: area.name,
                    icon: <ComposerAreaMark />
                }))}
                onPick={(id) => {
                    const picked = subjects.find((area) => area.id === id);
                    if (picked) chooseAreas([picked]);
                }}
            />
            <ComposerSelectionBody>
                <SelectionCommands>
                    <SelectionCommand onClick={() => moveAreas(subjects)}>
                        <MoveIcon />
                        <span>{T._('Move…', 'Admin')}</span>
                    </SelectionCommand>
                    <SelectionCommand onClick={() => cloneAreas(subjects)}>
                        <DuplicateIcon />
                        <span>{T._('Clone', 'Admin')}</span>
                    </SelectionCommand>
                    <ConfirmTwice onConfirm={() => removeAreas(subjects)}>
                        {({ armed, ...confirm }) => (
                            <SelectionCommand $danger {...confirm}>
                                <TrashIcon />
                                <span>{armed ? confirmLabel() : T._('Remove', 'Admin')}</span>
                            </SelectionCommand>
                        )}
                    </ConfirmTwice>
                </SelectionCommands>
            </ComposerSelectionBody>
        </ComposerPanel>
    );
};

const CustomizableAreasEditor: React.FC<{
    selectedAttribute: ComposerAttributeNode;
    onOpenFullEditor?: (area: ComposerAreaNode, templateNodeID: string) => Promise<boolean | void>;
}> = ({ selectedAttribute, onOpenFullEditor }) => {
    const [isOpeningFullEditor, setIsOpeningFullEditor] = React.useState(false);
    const [
        attributes,
        areas,
        templates,
        selectedAreas,
        sceneObjectList,
        sceneCameraList,
        attributeFace,
        updateAttribute,
        updateArea,
        setAttributeFace,
        validateAttributes
    ] = useComposerStore((store) => [
        store.attributes,
        store.areas,
        store.templates,
        store.selectedAreas,
        store.sceneObjectList,
        store.sceneCameraList,
        store.attributeFace,
        store.updateAttribute,
        store.updateArea,
        store.setAttributeFace,
        store.validateAttributes
    ]);
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    const model = useComposerStore((store) => store.model);
    const linkedAreas = areas
        .filter((area) => isComposerAreaLinkedToAttribute(area, selectedAttribute))
        .sort((left, right) => left.displayOrder - right.displayOrder);
    const selectedArea = selectedAreas?.find((area) => isComposerAreaLinkedToAttribute(area, selectedAttribute)) ?? null;
    const template = getComposerDefaultTemplate(templates, model?.get('defaultDesignTemplateID'));
    const templateSide = selectedArea && template?.design.get('sides').models.find((side) => side.get('sideID') === selectedArea.sideID);
    const sideRestrictions = selectedArea ? template?.restrictions.get(selectedArea.sideID) : undefined;
    const canAddText = sideRestrictions?.userCanAddTexts ?? templateSide?.get('canAddText') ?? true;
    const canAddImage = sideRestrictions?.userCanAddImages ?? templateSide?.get('canAddImage') ?? true;
    const allowedContent = canAddText && canAddImage ? 'both' : canAddText ? 'text' : canAddImage ? 'image' : 'none';
    // LE AREE STANNO NELL'ALBERO (round 4, pacchetto H): qui restano le tab dell'attributo.
    const activeTab = attributeFace === 'links' ? 1 : 0;

    const updateAllowedContent = (value: 'text' | 'image' | 'both') => {
        if (!template || !selectedArea) return;
        const update = useComposerStore.getState().updateTemplateRestriction;
        update(template, selectedArea.sideID, 'userCanAddTexts', value !== 'image');
        update(template, selectedArea.sideID, 'userCanAddImages', value !== 'text');
    };

    // L'area nasce come quella del «+» della riga, col nome del suo contenuto; poi il modello di design le dà
    // i permessi, gli stessi che «Allowed elements» scrive a mano.
    const addPresetArea = (content: 'text' | 'image' | 'both') => {
        const names = { text: T._('Text area', 'Admin'), image: T._('Image area', 'Admin'), both: T._('Area', 'Admin') };
        const area = addComposerArea(selectedAttribute.id, (mesh) => !!sceneEditor?.isRealMesh(mesh), names[content]);
        if (!area || !template) return;
        const update = useComposerStore.getState().updateTemplateRestriction;
        update(template, area.sideID, 'userCanAddTexts', content !== 'image');
        update(template, area.sideID, 'userCanAddImages', content !== 'text');
    };

    const updateSelectedAttribute = (changes: Partial<ComposerAttributeNode>) => {
        updateAttribute({ ...selectedAttribute, ...changes, isNew: true });
        if (changes.name !== undefined || changes.code !== undefined || changes.links !== undefined || changes.linkOperation !== undefined)
            void validateAttributes();
    };
    const updateSelectedAreaImage = async (imageUrl: string, tempImageFileKey?: string) => {
        if (!selectedArea) return '';
        const side = selectedArea.originalSide;
        side.set('imageUrl', imageUrl);
        side.set('tempImageFileKey', imageUrl ? (tempImageFileKey ?? '') : '');
        updateSelectedArea({
            img: imageUrl,
            originalSide: side,
            data: {
                ...selectedArea.data,
                side: side.toJSON()
            }
        });
        return imageUrl;
    };

    const updateSelectedArea = (changes: Partial<ComposerAreaNode>) => {
        if (!selectedArea) return;
        updateArea({ ...selectedArea, ...changes });
        if (changes.objects !== undefined) {
            void validateAttributes();
        }
    };
    // Keep non-printable scene nodes selectable: they can be associated with an area,
    // although they cannot display a printed texture in the Full Editor.
    const meshOptions: ComposerSlotOption[] = sceneObjectList.map((mesh) => {
        const isPrintable = !!sceneEditor?.isRealMesh(mesh) && isPrintableComposerMesh(mesh);
        return { value: mesh.id, label: mesh.name || mesh.id, caption: isPrintable ? undefined : T._('No printable geometry', 'Admin') };
    });
    const selectedMeshIds = selectedArea ? selectedArea.objects.split(';').filter(Boolean) : [];
    // Un oggetto che la scena non ha più resta nello slot, e lo dice: toglierlo è una scelta di chi guarda.
    const areaMeshOptions = [
        ...meshOptions,
        ...selectedMeshIds
            .filter((id) => !meshOptions.some((option) => option.value === id))
            .map((id) => ({ value: id, label: id, caption: T._('Unavailable in this scene', 'Admin') }))
    ];
    const cameraOptions = sceneCameraList.map((camera) => ({ value: camera.id, label: camera.name || camera.id }));
    const attributeNameHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.ATTRIBUTE_NAME_REQUIRED,
        ComposerValidationErrorCode.ATTRIBUTE_NAME_NOT_UNIQUE
    );
    const attributeCodeHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.ATTRIBUTE_CODE_REQUIRED,
        ComposerValidationErrorCode.ATTRIBUTE_CODE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );
    const areaObjectsHasError = hasComposerValidationError(
        selectedArea,
        ComposerValidationErrorCode.AREA_OBJECTS_REQUIRED,
        ComposerValidationErrorCode.AREA_OBJECTS_NOT_MESH
    );
    const openFullEditor = async () => {
        if (!selectedArea || !template || !onOpenFullEditor || isOpeningFullEditor) return;
        setIsOpeningFullEditor(true);
        try {
            await onOpenFullEditor(selectedArea, template.id);
        } finally {
            setIsOpeningFullEditor(false);
        }
    };

    // UN'AREA SCELTA NELL'ALBERO SI APRE QUI, al posto dell'attributo: l'Inspector è uno solo.
    if (selectedArea) {
        return (
            <ComposerPanel aria-label={T._('Area details', 'Admin')}>
                <ComposerSubjectHeader>
                    <ComposerSubjectMark $kind='option'>{selectedArea?.img ? <img src={selectedArea.img} alt='' /> : <AreaIcon />}</ComposerSubjectMark>
                    <ComposerSubjectContent>
                        <ComposerSubjectName>{selectedArea?.name || T._('Area details', 'Admin')}</ComposerSubjectName>
                        <ComposerSubjectMeta>
                            <ComposerKindBadge $kind='option'>{T._('Area', 'Admin')}</ComposerKindBadge>
                        </ComposerSubjectMeta>
                    </ComposerSubjectContent>
                    <ComposerRevealButton id={selectedArea.id} />
                </ComposerSubjectHeader>
                {/* LE IMPOSTAZIONI TECNICHE IN UNA TAB SUA (utente, 2026-10-15): canale UV e risoluzioni servono di
                    rado, e in coda al pannello lo allungavano fino a far scorrere tutto il resto. */}
                <ComposerTabs defaultIndex={0}>
                    <TabList>
                        <Tab>{T._('Settings', 'Admin')}</Tab>
                        <Tab>{T._('Advanced', 'Admin')}</Tab>
                    </TabList>
                    <TabPanel>
                        <ComposerSettingsBody>
                            <ComposerFormStack>
                                <ComposerSection id='area.preview-image' title={T._('Preview image', 'Admin')}>
                                    <ComposerUploadComponent passedImgUrl={selectedArea.img} onImagePick={updateSelectedAreaImage} />
                                </ComposerSection>
                                <ComposerSection id='area.placement' forceOpen={areaObjectsHasError} title={T._('Placement', 'Admin')}>
                                    <FormControl label={T._('Name', 'Admin')}>
                                        <Input value={selectedArea.name} onChange={(event) => updateSelectedArea({ name: event.target.value })} />
                                    </FormControl>
                                    <FormControl label={T._('Objects', 'Admin')}>
                                        <ComposerObjectSlots
                                            id={`composer-area-${selectedArea.id}-objects`}
                                            isError={areaObjectsHasError}
                                            value={selectedMeshIds}
                                            options={areaMeshOptions}
                                            onChange={(ids) => updateSelectedArea({ objects: ids.join(';') })}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Camera', 'Admin')}>
                                        <ComposerCameraSlot
                                            id={`composer-area-${selectedArea.id}-camera`}
                                            value={selectedArea.camera}
                                            options={cameraOptions}
                                            onChange={(camera) => updateSelectedArea({ camera })}
                                        />
                                    </FormControl>
                                </ComposerSection>
                                <ComposerSection id='area.size' title={T._('Size', 'Admin')}>
                                    <FormControl label={T._('Width (cm)', 'Admin')}>
                                        <PropertiesPanelNumberField
                                            full
                                            min={0.01}
                                            ariaLabel={T._('Width (cm)', 'Admin')}
                                            value={selectedArea.width}
                                            onChange={(width) => updateSelectedArea({ width: Math.max(0.01, width) })}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Height (cm)', 'Admin')}>
                                        <PropertiesPanelNumberField
                                            full
                                            min={0.01}
                                            ariaLabel={T._('Height (cm)', 'Admin')}
                                            value={selectedArea.height}
                                            onChange={(height) => updateSelectedArea({ height: Math.max(0.01, height) })}
                                        />
                                    </FormControl>
                                    <ComposerFieldNote>
                                        {T._(
                                            'Width and height define the physical print area in centimeters. Its outline in the Full Editor uses these proportions. New areas start at 10 × 10 cm.',
                                            'Admin'
                                        )}
                                    </ComposerFieldNote>
                                </ComposerSection>
                                <ComposerSection id='area.content' title={T._('Content', 'Admin')}>
                                    <FormControl
                                        label={T._('Allowed elements', 'Admin')}
                                        caption={T._(
                                            'Defaults to Both for new areas. More restrictions are available in the Full Editor → Template Settings.',
                                            'Admin'
                                        )}
                                    >
                                        <ComposerChoice
                                            ariaLabel={T._('Allowed elements', 'Admin')}
                                            options={[
                                                { value: 'text', label: T._('Text', 'Admin') },
                                                { value: 'image', label: T._('Images', 'Admin') },
                                                { value: 'both', label: T._('Both', 'Admin') }
                                            ]}
                                            value={allowedContent}
                                            onChange={(value) => value !== 'none' && updateAllowedContent(value)}
                                        />
                                        {allowedContent === 'none' && <small>{T._('No customer content is currently allowed for this area.', 'Admin')}</small>}
                                    </FormControl>
                                    <FormControl label={T._('Layout', 'Admin')}>
                                        <BasicButton disabled={!template || !onOpenFullEditor || isOpeningFullEditor} onClick={() => void openFullEditor()}>
                                            {isOpeningFullEditor ? T._('Saving…', 'Admin') : T._('Open Full Editor…', 'Admin')}
                                        </BasicButton>
                                    </FormControl>
                                    <ComposerUnsupportedNote>
                                        {T._(
                                            'The Full Editor edits this area only. New product and area settings are saved before it opens; template edits stay in the Composer draft until you press Save.',
                                            'Admin'
                                        )}
                                    </ComposerUnsupportedNote>
                                </ComposerSection>
                            </ComposerFormStack>
                        </ComposerSettingsBody>
                    </TabPanel>
                    <TabPanel>
                        <ComposerSettingsBody>
                            <ComposerFormStack>
                                <ComposerSection id='area.advanced' title={T._('Advanced', 'Admin')}>
                                    <FormControl label={T._('UV channel', 'Admin')}>
                                        <PropertiesPanelNumberField
                                            full
                                            min={0}
                                            step={1}
                                            ariaLabel={T._('UV channel', 'Admin')}
                                            value={selectedArea.uvMap}
                                            onChange={(uvMap) => updateSelectedArea({ uvMap: Math.max(0, uvMap) })}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Desktop resolution', 'Admin')}>
                                        {/* SENZA UN VALORE PROPRIO IL CAMPO MOSTRA QUELLO CHE IL MOTORE USA: 1024, che prima era il segnaposto.
                                            Il campo numerico condiviso non ha uno stato vuoto. */}
                                        <PropertiesPanelNumberField
                                            full
                                            min={1}
                                            step={1}
                                            ariaLabel={T._('Desktop resolution', 'Admin')}
                                            value={selectedArea.desktopTextureResolution ?? 1024}
                                            onChange={(desktopTextureResolution) =>
                                                updateSelectedArea({ customTextureResolution: true, desktopTextureResolution })
                                            }
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Mobile resolution', 'Admin')}>
                                        <PropertiesPanelNumberField
                                            full
                                            min={1}
                                            step={1}
                                            ariaLabel={T._('Mobile resolution', 'Admin')}
                                            value={selectedArea.mobileTextureResolution ?? 512}
                                            onChange={(mobileTextureResolution) =>
                                                updateSelectedArea({ customTextureResolution: true, mobileTextureResolution })
                                            }
                                        />
                                    </FormControl>
                                </ComposerSection>
                            </ComposerFormStack>
                        </ComposerSettingsBody>
                    </TabPanel>
                </ComposerTabs>
            </ComposerPanel>
        );
    }

    return (
        <ComposerPanel aria-label={T._('Customizable areas', 'Admin')}>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='attribute'>
                    <AreaIcon />
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Attribute name', 'Admin')}
                        isError={attributeNameHasError}
                        value={selectedAttribute.name}
                        onChange={(event) => updateSelectedAttribute({ name: event.target.value })}
                        onBlur={validateAttributes}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='attribute'>{T._('Customizable area', 'Admin')}</ComposerKindBadge>
                        <span>
                            {stringFormat(linkedAreas.length === 1 ? T._('{0} area', 'Admin') : T._('{0} areas', 'Admin'), linkedAreas.length.toString())}
                        </span>
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
                <ComposerRevealButton id={selectedAttribute.id} />
            </ComposerSubjectHeader>
            {/* «Add area» NON STA PIÙ QUI (utente, 2026-10-15): un'area nasce dal «+» della riga dell'attributo
                nell'albero (addComposerArea), come un'opzione. VUOTO, l'attributo offre da dove partire, come
                quello a opzioni: un'area di testo, d'immagine o di tutt'e due, già con i suoi permessi. */}
            {linkedAreas.length === 0 && (
                <ComposerPresetBox>
                    <span>{T._('This attribute has no areas yet. Start from:', 'Admin')}</span>
                    <ComposerPresetButtons>
                        <BasicButton $size='small' onClick={() => addPresetArea('text')}>
                            {T._('Text area', 'Admin')}
                        </BasicButton>
                        <BasicButton $size='small' onClick={() => addPresetArea('image')}>
                            {T._('Image area', 'Admin')}
                        </BasicButton>
                        <BasicButton $size='small' onClick={() => addPresetArea('both')}>
                            {T._('Text and image area', 'Admin')}
                        </BasicButton>
                    </ComposerPresetButtons>
                </ComposerPresetBox>
            )}
            <ComposerTabs selectedIndex={activeTab} onSelect={(index) => setAttributeFace(index === 1 ? 'links' : 'settings')}>
                <TabList hasOverflow>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                    <Tab>
                        {T._('Links', 'Admin')} <ComposerTabCount>{selectedAttribute.links.length}</ComposerTabCount>
                    </Tab>
                </TabList>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            <ComposerSection id='area-attribute.general' forceOpen={attributeCodeHasError} title={T._('General', 'Admin')}>
                                <FormControl label={T._('Code', 'Admin')}>
                                    <Input
                                        isError={attributeCodeHasError}
                                        value={selectedAttribute.code}
                                        onChange={(event) => updateSelectedAttribute({ code: event.target.value })}
                                    />
                                </FormControl>
                                <FormControl label={T._('Description', 'Admin')}>
                                    <TextArea
                                        rows={3}
                                        value={selectedAttribute.description}
                                        onChange={(event) => updateSelectedAttribute({ description: event.target.value })}
                                    />
                                </FormControl>
                            </ComposerSection>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
                <TabPanel>
                    <ComposerLinksEditor
                        attributes={attributes}
                        links={selectedAttribute.links}
                        logicalOperator={selectedAttribute.linkOperation}
                        selfAttributeID={selectedAttribute.id}
                        subjectLabel={selectedAttribute.name || T._('This attribute', 'Admin')}
                        validationErrors={selectedAttribute.brokenValues}
                        onChange={(links, linkOperation) => updateSelectedAttribute({ links, linkOperation })}
                    />
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

export default CustomizableAreasEditor;
